/**
 * BM-H4487 (19 Oct 2026): find out whether a save went through, instead of
 * asking the person to.
 *
 * Vanessa's director "Approve & Clear" for BL.VEC.006.1567 took about 52
 * seconds on the server (the matter folder is made inside the request). The
 * answer never reached the browser: something between the two gave up first
 * and returned a bare platform error. The screen then showed the amber "the
 * server did not confirm the save ... check the Pipeline Tracker before
 * saving again" warning, although the matter had been created in full. That
 * warning is what led staff to save again and make duplicate matters.
 *
 * The saves this wraps are safe to send again with the same key: the server
 * replays a finished save and answers 409 "conflict_check_save_unfinished"
 * while the first one is still running. So when the answer is lost we ask
 * again ourselves, quietly, until the server says the save is finished, and
 * only fall back to the warning if it still has not finished after a few
 * minutes.
 */

// About three minutes of asking in total.
export const CONFIRM_RETRY_DELAYS_MS = [4000, 6000, 10000, 15000, 20000, 25000, 30000, 30000, 30000];

/** No answer, or a platform 5xx that is not our API's own JSON answer. */
export function isUnconfirmedSave(err) {
  if (!err) return false;
  const r = err.response;
  if (!r) return true;
  const ourAnswer = r.data && typeof r.data === "object" && r.data.detail;
  return r.status >= 500 && !ourAnswer;
}

/** The server says an earlier press of this same save is still running. */
export function isSaveStillRunning(err) {
  const r = err && err.response;
  const d = r && r.data && r.data.detail;
  return !!(r && r.status === 409 && d && typeof d === "object"
    && d.code === "conflict_check_save_unfinished");
}

const defaultSleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Run ``attempt`` and, while its outcome is unknown or still running, run it
 * again after each delay. Resolves with ``{ value, retried }``; rejects with
 * the last error when the delays run out or the error is a real answer.
 */
export async function confirmSave(attempt, {
  delays = CONFIRM_RETRY_DELAYS_MS,
  sleep = defaultSleep,
  onWaiting = () => {},
} = {}) {
  for (let i = 0; ; i += 1) {
    try {
      const value = await attempt();
      return { value, retried: i > 0 };
    } catch (err) {
      const waitable = isUnconfirmedSave(err) || isSaveStillRunning(err);
      if (!waitable || i >= delays.length) throw err;
      onWaiting(i + 1, err);
      await sleep(delays[i]);
    }
  }
}
